// Rewrite Globo Search & Filter's "Sorry, nothing found for …" popup.
// Runs after Globo renders via the `globoFilterSearchCompleted` window event
// that Globo's CDN bundle dispatches when a search completes.
//
// Bonus: if the term looks like a UPC/GTIN (all digits, 6+ chars), hit
// /gtin?q=<term> and render the results inline inside the search drawer so
// the user can keep typing, editing, or clearing their query without the UI
// blocking them.

(function () {
  'use strict';

  const GTIN_LOOKUP_ENDPOINT = 'https://gtin-apimaxilite.maxilitecloudsolutions.store/gtin';
  const GTIN_MIN_DIGITS = 6;
  const INLINE_PANEL_ID = 'mx-gtin-inline-results';

  const gtinCache = new Map();
  const productCache = new Map();
  let inFlightTerm = null;
  let lastRenderedTerm = null;

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isGtinLikeTerm(term) {
    return /^\d+$/.test(term) && term.length >= GTIN_MIN_DIGITS;
  }

  function productUrlFromHandle(handle) {
    const slug = String(handle || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug ? `/products/${slug}` : '#';
  }

  // Find the best container inside the currently-open search drawer.
  // Prefer the <form> that owns the currently-focused / active search input —
  // appending inside the form (but outside Shopify's predictive-search results
  // div, which the <predictive-search> custom element nukes on every render)
  // keeps the panel alive while the user keeps typing.
  // Works for both the header drawer (.search-modal__form) and the /search
  // page's <main-search> form: we just walk up to closest('form') from
  // whatever input Globo decorated.
  function findInlineContainer() {
    const focused = document.querySelector(
      '.gf-autocomplete-input:focus, input[type="search"]:focus'
    );
    if (focused) {
      const form = focused.closest('form');
      if (form) return form;
    }

    const anyGlobo = document.querySelector('.gf-autocomplete-input');
    if (anyGlobo) {
      const form = anyGlobo.closest('form');
      if (form) return form;
    }

    return (
      document.querySelector('.search-modal__content') ||
      document.querySelector('.template-search__search') ||
      document.body
    );
  }

  function ensureInlinePanel(container) {
    let panel = document.getElementById(INLINE_PANEL_ID);
    if (panel && panel.parentElement !== container && container) {
      // Move to the correct container if the search drawer was re-rendered
      container.appendChild(panel);
    }
    if (!panel) {
      panel = document.createElement('div');
      panel.id = INLINE_PANEL_ID;
      (container || document.body).appendChild(panel);
    }

    // The header drawer's .search-modal__content is flex-centered, so an
    // in-flow panel grows the form and shoves the input upward out of view.
    // Detect that context and take the panel out of flow so the input stays
    // put. On /search (main-search), keep it in flow.
    const inHeaderModal = !!panel.closest('.search-modal__content');
    if (inHeaderModal && container) {
      container.style.position = 'relative';
    }
    const layoutStyles = inHeaderModal
      ? 'position:absolute;top:100%;left:0;right:0;margin-top:5px'
      : 'position:relative;margin-top:5px';

    panel.style.cssText = [
      layoutStyles,
      'z-index:10',
      'padding:14px 16px',
      'border:1px solid #d9d9d9',
      'border-radius:8px',
      'background:#fff',
      'box-shadow:0 4px 12px rgba(0,0,0,0.06)',
      'font-family:inherit',
      'color:#222',
    ].join(';');
    return panel;
  }

  function clearInlinePanel() {
    const panel = document.getElementById(INLINE_PANEL_ID);
    if (panel) panel.remove();
    lastRenderedTerm = null;
  }

  function buildCardHtml(r) {
    const img = r.variant_image || r.image || r.image_src || '';
    const url = productUrlFromHandle(r.handle);
    const title = escapeHtml(r.title || r.handle || '');
    const family = escapeHtml(r.family || '');
    const sku = escapeHtml(r.sku || '');
    const handleAttr = escapeHtml(r.handle || '');
    const skuRaw = escapeHtml(String(r.sku || '').trim());
    const cardKey = escapeHtml(`${r.handle || ''}__${String(r.sku || '').trim()}`);
    const safeImg = escapeHtml(img);

    const imgHtml = img
      ? `<img src="${safeImg}" alt="${title}" style="width:72px;height:72px;object-fit:contain;flex-shrink:0;background:#f5f5f5;border-radius:6px;">`
      : `<div style="width:72px;height:72px;flex-shrink:0;background:#f5f5f5;border-radius:6px;"></div>`;

    return (
      `<a href="${url}" class="mx-gtin-card" data-card-key="${cardKey}" data-handle="${handleAttr}" data-sku="${skuRaw}" style="display:flex;align-items:center;gap:14px;padding:10px;border:1px solid #e5e5e5;border-radius:8px;text-decoration:none;color:inherit;background:#fff;">` +
      imgHtml +
      `<div style="display:flex;flex-direction:column;gap:2px;min-width:0;flex:1;">` +
      `<strong class="mx-gtin-card__title" style="font-size:15px;line-height:1.25;">${title}</strong>` +
      `<span class="mx-gtin-card__family" style="font-size:12px;color:#555;${family ? '' : 'display:none;'}">Family: <span class="mx-gtin-card__family-value" style="color:#222;">${family}</span></span>` +
      (sku ? `<span style="font-size:12px;color:#555;">SKU: <span style="color:#222;">${sku}</span></span>` : '') +
      `</div>` +
      `<span style="font-size:18px;color:#999;flex-shrink:0;">&rsaquo;</span>` +
      `</a>`
    );
  }

  function slugifyHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async function fetchProductJson(slug) {
    if (productCache.has(slug)) return productCache.get(slug);
    console.log('[maxilite-globo-search-override] Shopify product fetch for slug:', slug);
    try {
      const res = await fetch(`/products/${slug}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[maxilite-globo-search-override] Shopify product response for slug:', slug, data);
      productCache.set(slug, data);
      return data;
    } catch (err) {
      console.warn('[maxilite-globo-search-override] /products/*.js fetch failed for', slug, err);
      productCache.set(slug, null);
      return null;
    }
  }

  function familyFromTags(tags) {
    if (!Array.isArray(tags)) return '';
    const tag = tags.find((t) => typeof t === 'string' && t.startsWith('Family_'));
    return tag ? tag.slice('Family_'.length).trim() : '';
  }

  async function enrichCardsFromShopify(results) {
    for (const r of results) {
      const slug = slugifyHandle(r.handle);
      if (!slug) continue;

      const product = await fetchProductJson(slug);

      // Race guard: bail if the panel was removed (user kept typing).
      if (!document.getElementById(INLINE_PANEL_ID)) return;
      if (!product) continue;

      const targetSku = String(r.sku || '').trim();
      const cardKey = `${r.handle || ''}__${targetSku}`;
      const card = document.querySelector(
        `#${INLINE_PANEL_ID} a[data-card-key="${cardKey.replace(/"/g, '\\"')}"]`
      );
      if (!card) continue;

      // Title from product.title
      if (product.title) {
        const titleEl = card.querySelector('.mx-gtin-card__title');
        if (titleEl) {
          titleEl.textContent = product.title;
          console.log('[maxilite-globo-search-override] Enriched title:', product.title);
        }
      }

      // Family from a "Family_<Name>" tag
      const family = familyFromTags(product.tags);
      const familyEl = card.querySelector('.mx-gtin-card__family');
      const familyValueEl = card.querySelector('.mx-gtin-card__family-value');
      if (familyEl && familyValueEl) {
        if (family) {
          familyValueEl.textContent = family;
          familyEl.style.display = '';
          console.log('[maxilite-globo-search-override] Enriched family:', family);
        } else {
          familyEl.style.display = 'none';
        }
      }

      // Variant URL upgrade
      if (Array.isArray(product.variants)) {
        const variant = product.variants.find(
          (v) => String(v.sku || '').trim() === targetSku
        );
        if (variant && variant.id) {
          const newHref = `/products/${slug}?variant=${variant.id}`;
          card.setAttribute('href', newHref);
          card.setAttribute('data-variant-id', String(variant.id));
          console.log('[maxilite-globo-search-override] Upgraded to variant URL:', newHref);
        } else {
          console.log(
            '[maxilite-globo-search-override] SKU not matched in product.variants — slug:',
            slug,
            'sku:',
            targetSku
          );
        }
      }
    }
  }

  function hideGloboPopup() {
    // While our inline panel holds the real answer, suppress Globo's
    // no-result popup so the user isn't looking at two competing messages.
    document.querySelectorAll('.glFilter-search-popup').forEach((el) => {
      el.style.display = 'none';
    });
  }

  function renderInline(term, results) {
    const container = findInlineContainer();
    const panel = ensureInlinePanel(container);
    const safeTerm = escapeHtml(term);

    panel.innerHTML =
      `<div style="font-size:13px;color:#666;margin-bottom:10px;">` +
      `Found ${results.length} match${results.length === 1 ? '' : 'es'} by UPC <b style="color:#222;">${safeTerm}</b>` +
      `</div>` +
      `<div style="display:flex;flex-direction:column;gap:8px;">` +
      results.map(buildCardHtml).join('') +
      `</div>`;

    hideGloboPopup();
    lastRenderedTerm = term;
    console.log('[maxilite-globo-search-override] Rendered', results.length, 'card(s) in container:', container);

    // Fire-and-forget: enrich each card with title + family + variant URL
    // from Shopify's public /products/{handle}.js.
    enrichCardsFromShopify(results);
  }

  async function tryGtinLookup(term) {
    if (gtinCache.has(term)) {
      const cached = gtinCache.get(term);
      if (cached && cached.length) renderInline(term, cached);
      else clearInlinePanel();
      return;
    }

    if (inFlightTerm === term) return;
    inFlightTerm = term;

    try {
      console.log('[maxilite-globo-search-override] GTIN lookup for term:', term);
      const url = `${GTIN_LOOKUP_ENDPOINT}?q=${encodeURIComponent(term)}`;
      const res = await fetch(url, { method: 'GET' });
      console.log('[maxilite-globo-search-override] Lookup HTTP status:', res.status);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const results = Array.isArray(data?.results) ? data.results : [];
      console.log('[maxilite-globo-search-override] Lookup returned', results.length, 'result(s)');
      gtinCache.set(term, results);

      // Race guard: only render if the user's term still matches
      const currentTerm = getCurrentPopupTerm();
      if (currentTerm && currentTerm !== term) {
        console.log('[maxilite-globo-search-override] Term changed mid-fetch, skipping render');
        return;
      }

      if (results.length) {
        renderInline(term, results);
      } else {
        clearInlinePanel();
      }
    } catch (err) {
      console.warn('[maxilite-globo-search-override] GTIN lookup failed:', err);
    } finally {
      if (inFlightTerm === term) inFlightTerm = null;
    }
  }

  function getCurrentPopupTerm() {
    const b = document.querySelector(
      '.glFilter-search-popup .gf-search-no-result .ui-menu-item-wrapper b'
    );
    return b ? b.textContent.trim() : '';
  }

  function customizeNoResult() {
    const wrapper = document.querySelector(
      '.glFilter-search-popup .gf-search-no-result .ui-menu-item-wrapper'
    );
    if (!wrapper) return;

    const term = (wrapper.querySelector('b')?.textContent || '').trim();
    const safeTerm = escapeHtml(term);

    console.log('[maxilite-globo-search-override] Rewriting no-result popup for term:', term);

    // --- Edit the copy below freely. Use `safeTerm` to include the search word. ---
    wrapper.innerHTML =
      `We couldn't find anything for <b>${safeTerm}</b>`;
    // ---------------------------------------------------------------------------

    const host = wrapper.closest('.gf-search-no-result');
    if (host) {
      host.setAttribute('aria-label', `We couldn't find anything for ${term}`);
    }

    document
      .querySelectorAll('.ui-helper-hidden-accessible > div')
      .forEach((el) => {
        if (el.textContent.trim().startsWith('Sorry, nothing found for')) {
          el.textContent = `We couldn't find anything for ${term}`;
        }
      });

    // Clear any stale inline panel whenever the term changes
    if (lastRenderedTerm && lastRenderedTerm !== term) clearInlinePanel();

    if (isGtinLikeTerm(term)) {
      tryGtinLookup(term);
    } else {
      // Non-GTIN term — remove any lingering panel from a previous GTIN search
      clearInlinePanel();
    }
  }

  // Clear the inline panel when the user edits the search input, so stale
  // results don't linger mid-typing.
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!t || !t.matches || !t.matches('input[type="search"], .gf-autocomplete-input')) return;
    const val = (t.value || '').trim();
    if (val !== lastRenderedTerm) clearInlinePanel();
  });

  window.addEventListener('globoFilterSearchCompleted', customizeNoResult);

  if (document.readyState === 'complete') {
    customizeNoResult();
  } else {
    window.addEventListener('load', customizeNoResult);
  }
})();
